import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@achra/ui/dropdown-menu';
import { cn } from '@achra/ui/lib/utils';
import { EllipsisVertical } from 'lucide-react';
export interface ActionsAreaProps {
  children: React.ReactNode;
}

/**
 * Container for navbar action items (right side)
 */
function ActionsArea(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div>{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
ActionsArea.displayName = 'NavbarActionsArea';
export interface ActionsGroupProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Groups related action items with consistent spacing
 */
function ActionsGroup(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-center gap-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
ActionsGroup.displayName = 'NavbarActionsGroup';

/**
 * Visual separator between action items
 */
function ActionSeparator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="bg-border h-9 w-px" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
ActionSeparator.displayName = 'NavbarActionSeparator';
export interface ActionWithOptionsProps {
  children: React.ReactNode;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Dropdown menu container for action options (mobile)
 */
function ActionWithOptions(t0) {
  const $ = _c(12);
  const {
    children,
    className,
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-center", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DropdownMenuTrigger asChild={true}><Button variant="outline" size="icon" aria-label="Open menu"><EllipsisVertical /><span className="sr-only">Open menu</span></Button></DropdownMenuTrigger>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children) {
    t3 = <DropdownMenuContent className="z-160 w-56 md:hidden" align="end">{children}</DropdownMenuContent>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onOpenChange || $[6] !== open || $[7] !== t3) {
    t4 = <DropdownMenu open={open} onOpenChange={onOpenChange}>{t2}{t3}</DropdownMenu>;
    $[5] = onOpenChange;
    $[6] = open;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className={t1}>{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
ActionWithOptions.displayName = 'NavbarActionWithOptions';
export type ActionOptionProps = React.ComponentProps<typeof DropdownMenuItem>;

/**
 * Individual action option in dropdown menu
 */
function ActionOption(t0) {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("cursor-pointer", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <DropdownMenuItem className={t1} {...props}>{children}</DropdownMenuItem>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
ActionOption.displayName = 'NavbarActionOption';

/**
 * Separator between action options in dropdown menu
 */
function ActionOptionSeparator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DropdownMenuSeparator />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
ActionOptionSeparator.displayName = 'NavbarActionOptionSeparator';
export { ActionsArea, ActionsGroup, ActionSeparator, ActionWithOptions, ActionOption, ActionOptionSeparator };